import React, { useState, useEffect, useRef, useCallback } from "react";
import { router } from "@inertiajs/react";
import {
  Dialog,
  DialogContent,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { 
  Maximize2, 
  Minimize2, 
  Save, 
  X, 
  Eye, 
  Code, 
  Download, 
  Check,
  FileCode,
  FileText,
  Loader2
} from "lucide-react";
import { cn } from "../lib/utils";
import { toast } from "sonner";
import { htmlToPlainText } from "../lib/htmlToText";

interface Template {
  id: number;
  name: string;
  html_content: string | null;
  text_content: string | null;
  folder_id: number;
  folder_name: string;
  uploaded_by: string;
  created_at: string;
}

interface EditTemplateDialogProps {
  template: Template | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  offerId: number;
}

export default function EditTemplateDialog({
  template,
  open,
  onOpenChange,
  offerId,
}: EditTemplateDialogProps) {
  const [editedContent, setEditedContent] = useState("");
  const [editedText, setEditedText] = useState("");
  // What we last auto-generated, so a hand-edited text is never overwritten
  const [lastGeneratedText, setLastGeneratedText] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<"html" | "text">("html");
  const [previewContent, setPreviewContent] = useState("");
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [splitRatio, setSplitRatio] = useState(50);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const isDragging = useRef(false);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (template && open) {
      const html = template.html_content || "";
      const savedText = template.text_content || "";
      setEditedContent(html);
      setPreviewContent(html);
      setEditedText(savedText);
      // If the saved text matches what the HTML would generate, treat it as
      // auto-generated (keep syncing). If it's custom, leave it alone.
      const generated = html.trim() ? htmlToPlainText(html) : "";
      setLastGeneratedText(savedText === generated ? generated : null);
      // Text-only templates should not open on an empty HTML editor
      setActiveTab(template.html_content ? "html" : "text");
      setHasChanges(false);
      setIsUpdating(false);
    }
  }, [template, open]);

  // Latest values for the debounced sync effect, without making them deps
  const editedTextRef = useRef(editedText);
  const lastGeneratedRef = useRef(lastGeneratedText);
  editedTextRef.current = editedText;
  lastGeneratedRef.current = lastGeneratedText;

  // Auto-generate the text part when the HTML changes, unless the text has been
  // hand-edited. Mirrors the upload panel's behavior.
  useEffect(() => {
    if (!open) return;

    const untouched =
      !editedTextRef.current.trim() ||
      editedTextRef.current === lastGeneratedRef.current;
    if (!untouched) return;

    if (!editedContent.trim()) {
      if (editedTextRef.current) {
        handleTextChange("");
        setLastGeneratedText("");
      }
      return;
    }

    const timer = setTimeout(() => {
      const generated = htmlToPlainText(editedContent);
      if (generated !== editedTextRef.current) {
        handleTextChange(generated);
        setLastGeneratedText(generated);
      }
    }, 400);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editedContent, open]);

  const updatePreviewWithDebounce = useCallback((content: string) => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    
    setIsUpdating(true);
    
    debounceTimerRef.current = setTimeout(() => {
      setPreviewContent(content);
      setIsUpdating(false);
    }, 300);
  }, []);

  const handleContentChange = (value: string) => {
    setEditedContent(value);
    setHasChanges(
      value !== (template?.html_content || "") ||
        editedText !== (template?.text_content || "")
    );
    if (value !== (template?.html_content || "")) {
      updatePreviewWithDebounce(value);
    }
  };

  const handleTextChange = (value: string) =>{
    setEditedText(value);
    setHasChanges(
      value !== (template?.text_content || "") ||
      editedContent !== (template?.html_content || "")
    );
  }

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const handleDownload = () => {
    const isText = activeTab === "text";
    const blob = new Blob([isText ? editedText : editedContent], {
      type: isText ? "text/plain" : "text/html",
    });
    const ext = isText ? "txt" : "html";
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${template?.name || "template"}.${ext}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);

    toast.success(`Downloaded ${template?.name || "template"}.${ext}`, {
      duration: 3000,
      icon: <Download className="h-4 w-4" />,
    });
  };

  const handleSave = async () => {
    if (!template || !hasChanges) return;
    
    setIsSaving(true);
    
    const toastId = toast.loading("Saving template...");
    
    router.patch(
      `/offers/${offerId}/templates/${template.id}`,
      {
        html_content: editedContent,
        // Empty contentEditable markup ("<br>" etc.) should clear the field
        text_content: editedText.trim(),
      },
      {
        onSuccess: () => {
          setHasChanges(false);
          setIsSaving(false);
          toast.success("Template saved successfully!", {
            id: toastId,
            duration: 3000,
            icon: <Check className="h-4 w-4" />,
            description: `${template.name}.html has been updated`,
          });
        },
        onError: () => {
          setIsSaving(false);
          toast.error("Failed to save template", {
            id: toastId,
            duration: 5000,
            description: "Please try again or check your connection",
          });
        },
      }
    );
  };

  const handleClose = () => {
    if (hasChanges) {
      const confirmClose = window.confirm(
        "You have unsaved changes. Are you sure you want to close?"
      );
      if (!confirmClose) return;
    }
    onOpenChange(false);
  };

  const handleDialogOpenChange = (open: boolean) => {
    if (!open && hasChanges) {
      const confirmClose = window.confirm(
        "You have unsaved changes. Are you sure you want to close?"
      );
      if (!confirmClose) return;
    }
    onOpenChange(open);
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    isDragging.current = true;
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (!isDragging.current) return;
    
    const dialogContent = document.querySelector('.edit-template-content');
    if (!dialogContent) return;
    
    const rect = dialogContent.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const newRatio = (x / rect.width) * 100;
    
    if (newRatio >= 20 && newRatio <= 80) {
      setSplitRatio(newRatio);
    }
  };

  const handleMouseUp = () => {
    isDragging.current = false;
  };

  useEffect(() => {
    if (open) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [open]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Tab') {
      e.preventDefault();
      const textarea = e.currentTarget;
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      
      const newValue = 
        editedContent.substring(0, start) +
        '  ' +
        editedContent.substring(end);
      
      setEditedContent(newValue);
      
      setTimeout(() => {
        textarea.selectionStart = textarea.selectionEnd = start + 2;
      }, 0);
    }
    
    if ((e.ctrlKey || e.metaKey) && e.key === 's') {
      e.preventDefault();
      handleSave();
    }
  };

  if (!template) return null;

  return (
    <Dialog open={open} onOpenChange={handleDialogOpenChange} modal={true}>
      <DialogContent
        className={cn(
          "edit-template-content p-0 overflow-hidden transition-all duration-300 flex flex-col shadow-2xl",
          isFullscreen
            ? "!max-w-[100vw] !w-screen !h-screen !rounded-none"
            : "!max-w-7xl w-full h-[90vh] rounded-xl"
        )}
        showCloseButton={false}
      >
        <div className="px-6 py-4 border-b bg-gradient-to-r from-slate-50 to-slate-100/50 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="p-2 bg-slate-100 rounded-lg">
              <FileCode className="h-5 w-5 text-slate-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-900">{template.name}.html</h2>
              <p className="text-xs text-slate-500 mt-0.5">HTML Email Template Editor</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {hasChanges && (
              <div className="flex items-center gap-1.5 px-2.5 py-1 bg-amber-50 border border-amber-200 rounded-md">
                <div className="w-1.5 h-1.5 bg-amber-500 rounded-full" />
                <span className="text-xs text-amber-700 font-medium">
                  Unsaved
                </span>
              </div>
            )}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsFullscreen(!isFullscreen)}
              className="h-8 w-8 hover:bg-slate-200/70 transition-colors"
              title={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
            >
              {isFullscreen ? (
                <Minimize2 className="h-4 w-4 text-slate-600" />
              ) : (
                <Maximize2 className="h-4 w-4 text-slate-600" />
              )}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleClose}
              className="h-8 w-8 hover:bg-slate-200/70 transition-colors"
              title="Close editor"
            >
              <X className="h-4 w-4 text-slate-600" />
            </Button>
          </div>
        </div>

        <div className="flex-1 flex overflow-hidden">
          <div
            className="flex-shrink-0 bg-slate-50 flex flex-col"
            style={{ width: `${splitRatio}%` }}
          >
            <div className="px-4 py-3 bg-white border-b flex items-center justify-between">
              <div className="flex items-center gap-1.5">
                <button
                  onClick={() => setActiveTab("html")}
                  className={cn(
                    "flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors cursor-pointer",
                    activeTab === "html"
                      ? "bg-slate-100 text-slate-900"
                      : "text-slate-500 hover:bg-slate-50"
                  )}
                >
                  <Code className="h-3.5 w-3.5" />
                  HTML
                </button>
                <button
                  onClick={() => setActiveTab("text")}
                  className={cn(
                    "flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors cursor-pointer",
                    activeTab === "text"
                      ? "bg-slate-100 text-slate-900"
                      : "text-slate-500 hover:bg-slate-50"
                  )}
                >
                  <FileText className="h-3.5 w-3.5" />
                  Text
                  {!editedText.trim() && (
                    <span className="text-[10px] text-slate-400">empty</span>
                  )}
                </button>
              </div>
            </div>
            <div className="flex-1 p-3 overflow-hidden">
              {activeTab === "html" ? (
                <div className="h-full bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
                  <textarea
                    ref={textareaRef}
                    value={editedContent}
                    onChange={(e) => handleContentChange(e.target.value)}
                    onKeyDown={handleKeyDown}
                    className="w-full h-full font-mono text-[13px] leading-relaxed p-4 resize-none focus:outline-none text-slate-800 placeholder-slate-400"
                    style={{ tabSize: 2 }}
                    spellCheck={false}
                    placeholder="Enter your HTML content here..."
                  />
                </div>
              ) : (
                <div className="h-full bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
                  <textarea
                    value={editedText}
                    onChange={(e) => handleTextChange(e.target.value)}
                    className="w-full h-full font-mono text-[13px] leading-relaxed p-4 resize-none focus:outline-none text-slate-800 placeholder-slate-400"
                    spellCheck={false}
                    placeholder="The plain-text version shown by email clients that can't render HTML. Write it here, or generate it from the HTML."
                  />
                </div>
              )}
            </div>
          </div>

          <div className="relative">
            <div
              className="w-1 bg-gradient-to-b from-slate-200 to-slate-300 hover:from-blue-300 hover:to-blue-400 cursor-col-resize transition-all duration-200 group"
              onMouseDown={handleMouseDown}
              style={{ cursor: 'col-resize' }}
            >
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-8 h-12 flex items-center justify-center">
                <div className="w-0.5 h-8 bg-slate-400 group-hover:bg-blue-500 transition-colors rounded-full" />
              </div>
            </div>
          </div>

          <div className="flex-1 flex flex-col overflow-hidden bg-slate-50">
            <div className="px-4 py-3 bg-white border-b flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <div className="p-1.5 bg-slate-100 rounded">
                  <Eye className="h-3.5 w-3.5 text-slate-600" />
                </div>
                <span className="text-sm font-medium text-slate-800">Preview</span>
                <span className="text-xs text-slate-500">Auto-updates as you type</span>
                {isUpdating && activeTab === "html" && (
                  <Loader2 className="h-3 w-3 text-blue-500 animate-spin" />
                )}
              </div>
            </div>
            <div className="flex-1 p-3 overflow-auto">
              <div className="w-full h-full bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
                <div className="h-full relative">
                  {activeTab === "html" ? (
                    <iframe
                      srcDoc={previewContent}
                      className="w-full h-full border-0"
                      sandbox="allow-same-origin"
                      title="Template Preview"
                    />
                  ) : (
                    <div className="w-full h-full overflow-auto text-sm leading-relaxed text-slate-800">
                      {editedText.trim() ? (
                        <pre className="p-4 font-mono text-[13px] leading-relaxed whitespace-pre-wrap break-words">
                          {editedText}
                        </pre>
                      ) : (
                        <span className="block p-4 text-slate-400">
                          No text content yet.
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="px-6 py-4 border-t bg-gradient-to-r from-slate-50 to-slate-100/50 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleDownload}
              className="h-8 px-3 hover:bg-slate-200/70 transition-colors"
              title="Download current HTML"
            >
              <Download className="w-3.5 h-3.5 mr-2 text-slate-600" />
              <span className="text-sm text-slate-700">Download</span>
            </Button>
          </div>
          <div className="flex items-center gap-3">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleClose} 
              className="h-9 px-4 border-slate-200 text-slate-700 hover:bg-slate-100 hover:border-slate-300 transition-all"
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleSave}
              disabled={!hasChanges || isSaving}
              className={cn(
                "h-9 px-4 font-medium transition-all",
                hasChanges
                  ? "bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white shadow-sm"
                  : "bg-slate-200 text-slate-400 cursor-not-allowed"
              )}
            >
              <Save className="w-3.5 h-3.5 mr-2" />
              {isSaving ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}